// frontend/src/pages/TradePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axiosAuth from "../axiosConfig";

/** ---------- Helpers (robust detection) ---------- */
const TOKEN_KEYS = ["token", "authToken", "userToken", "accessToken", "jwt", "jwtToken"];
function getTokenFromLocalStorage() {
  if (typeof window === "undefined") return null;
  for (const k of TOKEN_KEYS) {
    const v = localStorage.getItem(k);
    if (v && typeof v === "string" && v.trim().length > 10) return v;
  }
  return null;
}
function getUserFromLocalStorage() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("user") || localStorage.getItem("whoami");
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (u && (u._id || u.id || u.userId || u.email)) return u;
  } catch {}
  return null;
}
function getTokenFromCookies() {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)(token|authToken|jwt|accessToken)=([^;]+)/i);
  return m ? decodeURIComponent(m[2]) : null;
}

/** Decide initial state fast (no network) */
function initialAuthGuess() {
  const token = getTokenFromLocalStorage() || getTokenFromCookies();
  const user  = getUserFromLocalStorage();
  if (user) return true;
  if (token) return true;
  return false;
}

export default function TradePage() {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(initialAuthGuess());
  const [checked, setChecked]   = useState(false);

  // Verify with whoami (non-blocking)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosAuth.get("/auth/whoami");
        const ok =
          !!res?.data?._id ||
          !!res?.data?.id ||
          !!res?.data?.userId ||
          !!res?.data?.email;
        if (!cancelled) setLoggedIn(ok);
      } catch (e) {
        if (!cancelled) setLoggedIn(false);
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <motion.div
      className="min-h-screen bg-blue-50 text-slate-800 dark:bg-slate-900 dark:text-slate-100 pb-12 sm:pb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero */}
      <header className="bg-white/90 dark:bg-slate-900/90 backdrop-blur shadow-md border-b-4 border-blue-600 dark:border-blue-500">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-14 text-center">
          <h1 className="text-3xl sm:text-5xl font-bold text-blue-800 dark:text-blue-300 mb-2 sm:mb-4">
            Trade on Coinzetto
          </h1>
          <p className="text-blue-700/90 dark:text-blue-200/90 text-sm sm:text-xl max-w-2xl mx-auto">
            Seamlessly buy, sell, and swap cryptocurrencies with INR or USDT—fast, secure, and reliable.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 sm:mt-16 space-y-10 sm:space-y-20">
        {/* Trading Methods */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-3 sm:mb-6">
            Ways to Trade
          </h2>
          <div className="space-y-4 sm:space-y-6 text-slate-700 dark:text-slate-300 text-sm sm:text-base">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 sm:p-6 shadow-sm">
              <h3 className="text-lg sm:text-2xl font-semibold text-blue-700 dark:text-blue-300 mb-1 sm:mb-2">
                🔁 Quick Trade
              </h3>
              <p>
                Quick Trade is ideal for beginners and those who want instant execution. Simply choose your coin, enter the amount, and execute
                at the current market price. No order books or complexity.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 sm:p-6 shadow-sm">
              <h3 className="text-lg sm:text-2xl font-semibold text-blue-700 dark:text-blue-300 mb-1 sm:mb-2">
                📈 Exchange Trade
              </h3>
              <p>
                For more advanced users, our Exchange page supports full order book trading, including both market and limit orders. You can set
                your own price and manage open orders in real time.
              </p>
            </div>
          </div>
        </section>

        {/* Roadmap */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-3 sm:mb-6">
            What's Coming Next
          </h2>
          <ul className="space-y-3 sm:space-y-5 text-slate-700 dark:text-slate-300 text-sm sm:text-base">
            <li className="flex items-start rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 sm:p-5 shadow-sm">
              <span className="mr-3 sm:mr-4 text-blue-600 dark:text-blue-300 text-2xl sm:text-3xl">📊</span>
              <div className="pt-0.5">
                <strong>Futures Trading:</strong> Soon, you'll be able to open leveraged long and short positions on major crypto pairs.
              </div>
            </li>
            <li className="flex items-start rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 sm:p-5 shadow-sm">
              <span className="mr-3 sm:mr-4 text-blue-600 dark:text-blue-300 text-2xl sm:text-3xl">🌐</span>
              <div className="pt-0.5">
                <strong>Spot Markets Expansion:</strong> We're adding dozens of new coins and trading pairs including INR/ALT and USDT/ALT combinations.
              </div>
            </li>
            <li className="flex items-start rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 sm:p-5 shadow-sm">
              <span className="mr-3 sm:mr-4 text-blue-600 dark:text-blue-300 text-2xl sm:text-3xl">📱</span>
              <div className="pt-0.5">
                <strong>Mobile App + Live Charts:</strong> A complete trading experience on the go, with integrated TradingView charts and real-time data.
              </div>
            </li>
          </ul>
        </section>

        {/* Benefits */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-3 sm:mb-6">
            Why Trade on Coinzetto?
          </h2>
          <ul className="space-y-3 sm:space-y-5 text-slate-700 dark:text-slate-300 text-sm sm:text-base">
            {[
              { icon: "⚡", title: "Instant Execution:", text: "Fast market orders on Quick Trade for time-sensitive buying and selling." },
              { icon: "🧠", title: "Smart Order Routing:", text: "Our engine ensures best prices by checking available liquidity across internal and partner sources." },
              { icon: "🛡️", title: "Safe & Compliant:", text: "Regulated platform with KYC/AML in place to keep your trading legal and secure." },
            ].map((b) => (
              <li
                key={b.title}
                className="flex items-start rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 sm:p-5 shadow-sm"
              >
                <span className="mr-3 sm:mr-4 text-blue-600 dark:text-blue-300 text-2xl sm:text-3xl">{b.icon}</span>
                <div className="pt-0.5">
                  <strong>{b.title}</strong> {b.text}
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="text-center pt-4 sm:pt-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-2 sm:mb-4">
            Start Trading Today
          </h2>
          <p className="text-slate-700 dark:text-slate-300 text-sm sm:text-base mb-4 sm:mb-6 max-w-xl mx-auto">
            It takes less than a minute to get started. Fund your INR or USDT wallet and begin trading instantly.
          </p>

          {loggedIn ? (
            <button
              className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 sm:px-8 sm:py-3 text-sm sm:text-base font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 dark:focus-visible:ring-blue-500 shadow"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
            </button>
          ) : (
            <button
              className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 sm:px-8 sm:py-3 text-sm sm:text-base font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 dark:focus-visible:ring-blue-500 shadow"
              onClick={() => navigate("/signup")}
            >
              Open Your Account
            </button>
          )}
        </section>
      </main>
    </motion.div>
  );
}
